import React, { useState, useContext, useEffect } from "react"
import secureLocalStorage from "react-secure-storage"
import CharLimit from "../ui/charLimit"
import CloseModal from "../CloseModal"
import EditSocialMedia from "./EditSocialMedia"
import Loader from "../ui/loader"
import Select from "react-select"
import TagsModal from "./TagsModal"
import { Button } from "../ui/button"
import { CoachPrivateProfileContext } from "../../pages/manage-business/profile/context/coachPrivateProfileContext"
import { getLightTextColor } from "../../utils/getLightTextColor"
import { ProfileRequests } from "../../api/app.service"
import { regions } from "../../objects/regionObjects"
import { SocialMediaLink } from "../../models/app.interface"
import { SvgCheck, SvgChevronRight, SvgEdit, SvgWarning } from "../icons"
import { useToast } from "../../hooks/useToast"

const EditProfileInformation: React.FC = () => {
  const [loadingSlugVerification, setLoadingSlugVerification] =
    useState<boolean>(false)
  const { toast } = useToast()
  const {
    setEditCredentials,
    editSocialMedia,
    setEditSocialMedia,
    coachPublicProfileData,
    getCoachPrivateProfile,
    setEditProfileInformation,
    selectedTags,
  } = useContext(CoachPrivateProfileContext)
  const [customColor, setCustomColor] = useState<string>("")
  const allowedColors = [
    "#4750F5",
    "#7291FF",
    "#1DB685",
    "#FFAF43",
    "#9057BD",
    "#63696F",
  ]
  const [endpointSlugTaken, setEndpointSlugTaken] = useState<boolean>(false)
  const [socialLinks, setSocialLinks] = useState<SocialMediaLink[]>([])
  const [tagModal, setTagModal] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [tagsUpdated, setTagsUpdated] = useState<boolean>(false)
  const [tempEndpointSlug, setTempEndpointSlug] = useState<string>(
    coachPublicProfileData?.endpoint_slug
  )
  // POPULATE FORM VALUES ON COMPONENT LOAD WITH PROFILE DATA
  const [formValues, setFormValues] = useState<any>({
    profileColor: "",
    endpointSlug: "",
    title: "",
    description: "",
    about: "",
    yearsExperience: 0,
  })
  // FORM ERRORS
  const [formErrors, setFormErrors] = useState<any>({})
  const [isSubmit, setIsSubmit] = useState<boolean>(false)
  const [urlChecked, setUrlChecked] = useState<boolean>(true)

  const getSocialMedia = async () => {
    try {
      const data = await ProfileRequests.getSocialMedia({
        coach_public_profile_id: coachPublicProfileData.coach_public_profile_id,
      })
      setSocialLinks(data.links)
    } catch (ex: any) {
      console.error(ex)
      toast.error(ex.response?.data?.message || "An error occurred.")
    }
  }

  const validateForm = () => {
    const hexPattern = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/
    const errors: any = {}

    if (!formValues.profileColor || !hexPattern.test(formValues.profileColor)) {
      errors.profileColor = "Valid hex code required"
    }

    !formValues.title && (errors.title = "Title is required")
    !formValues.description && (errors.description = "Description is required")

    if (!formValues.endpointSlug || endpointSlugTaken) {
      errors.endpointSlug = "A unique profile URL endpoint is required."
    }

    return errors
  }

  const handleColorSelection = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    const target = e.target as HTMLButtonElement
    setFormValues({ ...formValues, profileColor: target.dataset.color })
  }
  const handleCustomColorSelection = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormValues({ ...formValues, profileColor: e.target.value })
    setCustomColor(e.target.value)
  }
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormValues({ ...formValues, [e.target.name]: e.target.value })
  }

  const handleChangeSlug = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTempEndpointSlug(e.target.value)
    setUrlChecked(false)
  }

  const handleChangeTextArea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormValues({ ...formValues, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e: React.FormEvent<EventTarget>) => {
    e.preventDefault()
    setLoading(true)

    if (
      tempEndpointSlug ===
      coachPublicProfileData?.profile_information.endpointSlug
    ) {
      setEndpointSlugTaken(false)
    } else {
      const isUrlAvailable = await checkUrlAvailability()
      if (!isUrlAvailable) {
        setLoading(false)
        return
      }
    }

    setFormErrors(validateForm())
    setIsSubmit(true)
  }

  const updateProfileInformation = () => {
    if (formValues !== coachPublicProfileData?.profile_information) {
      ProfileRequests.editProfileInformation({
        coach_public_profile_id: parseInt(
          coachPublicProfileData.coach_public_profile_id
        ),
        profile_information: formValues,
        tags: selectedTags,
        endpoint_slug: formValues.endpointSlug.toLowerCase(),
      })
        .then(() => {
          setLoading(false)
          toast.success("We've saved your progress!")
          setEditProfileInformation(false)
          getCoachPrivateProfile()

          const rawUser = secureLocalStorage.getItem("user")
          let user = typeof rawUser === "string" ? JSON.parse(rawUser) : {}
          if (user && typeof user === "object") {
            user.endpoint_slug = formValues.endpointSlug.toLowerCase()

            if (user.coachProfile && typeof user.coachProfile === "object") {
              user.coachProfile.endpoint_slug =
                formValues.endpointSlug.toLowerCase()
            }

            secureLocalStorage.setItem("user", JSON.stringify(user))
          }
        })
        .catch((ex) => {
          console.error(ex)
          toast.error(ex.response.data.message)
        })
    } else {
      toast.error("No changes made!")
    }
  }

  const checkUrlAvailability = async (): Promise<boolean> => {
    if (!tempEndpointSlug.match(/^[0-9a-zA-Z_-]{6,30}$/)) {
      setTempEndpointSlug(formValues?.endpointSlug)
      toast.error(
        "Website URL should be between six and 30 characters, and can contain numbers, letters, dashes and underscores"
      )
      return false
    }

    if (tempEndpointSlug === formValues?.endpointSlug) {
      return true
    }

    const lowercaseSlug = tempEndpointSlug?.toLowerCase()
    setLoadingSlugVerification(true)

    try {
      const data = await ProfileRequests.checkUrlAvailability({
        coach_public_profile_id: parseInt(
          coachPublicProfileData.coach_public_profile_id
        ),
        endpoint_slug: lowercaseSlug,
      })

      setLoadingSlugVerification(false)

      if (data.endpoint_available) {
        setEndpointSlugTaken(false)
        setFormValues({ ...formValues, endpointSlug: tempEndpointSlug })
        setUrlChecked(true)
        return true
      } else {
        setEndpointSlugTaken(true)
        setUrlChecked(false)
        setTempEndpointSlug(formValues?.endpointSlug)
        toast.error("This website URL is in use!")
        return false
      }
    } catch (ex: any) {
      setLoadingSlugVerification(false)
      console.error(ex)
      toast.error(ex?.response?.data?.message || "Error checking slug.")
      return false
    }
  }

  const setInitialProfileColor = () => {
    const profileColor =
      coachPublicProfileData?.profile_information?.profileColor
    return profileColor || allowedColors[0]
  }

  useEffect(() => {
    if (isSubmit && Object.keys(formErrors).length === 0) {
      updateProfileInformation()
      setIsSubmit(false)
    } else {
      setIsSubmit(false)
    }
  }, [isSubmit])

  useEffect(() => {
    if (tagsUpdated) {
      getCoachPrivateProfile()
      setTagsUpdated(false)
    }
  }, [tagsUpdated])

  useEffect(() => {
    if (coachPublicProfileData) {
      setFormValues({
        ...coachPublicProfileData?.profile_information,
        endpointSlug: coachPublicProfileData?.endpoint_slug,
        profileColor: setInitialProfileColor(),
      })
    }
  }, [])

  return (
    <div className="fixed top-0 left-0 w-screen h-full flex items-center justify-center zoee-backdrop-filter z-[1001] ">
      <div className="w-[800px] max-w-[90%] main-shadow rounded-[16px] pt-[103px] pb-[40px] max-h-[85vh] bg-white fixed left-[50%] -translate-x-[50%] top-[60px] flex flex-col items-start">
        <div className="relative w-full flex flex-col items-center mb-8 -mt-16 sm:-mt-12">
          <CloseModal
            callback={() => setEditProfileInformation(false)}
            styling="absolute top-0 right-6 sm:right-12 flex items-center justify-center w-[32px] h-[32px] rounded-full bg-grayFlash"
          />

          <div className="text-center">
            <h2 className="text-[18px]">Edit</h2>
            <h2 className="font-bold text-[20px]">Intro & About Info</h2>
          </div>
        </div>

        {/* FORM */}
        <form className="overflow-y-auto w-full px-[12px] md:px-[122px] pb-[80px] border">
          <div className="flex flex-col gap-[24px] w-[98%] mx-auto">
            {/* BRAND COLOR */}
            <div className="flex flex-col sm:flex-row gap-[16px] items-start w-full justify-between">
              <div className="w-full pr-[16px]">
                <h2 className="text-[16px] font-bold">Website Brand Color</h2>

                <div className="flex justify-center mt-4">
                  {allowedColors.map((color) => (
                    <div
                      className={`w-16 h-10 m-2 flex flex-row rounded-[12px] gap-0.5 ${
                        color === formValues.profileColor
                          ? `border-2 border-blurple rounded-[14px]`
                          : ``
                      } hover:border hover:border-grayCloud hover:border-2 shadow-md hover:scale-110 transform transition-transform`}
                      key={color}
                    >
                      <button
                        onClick={handleColorSelection}
                        data-color={color}
                        className={`w-1/2 h-full rounded-l-[12px]`}
                        style={{ backgroundColor: color }}
                      ></button>
                      <button
                        onClick={handleColorSelection}
                        data-color={color}
                        className={`w-1/2 h-full rounded-r-[12px]`}
                        style={{ backgroundColor: getLightTextColor(color) }}
                      ></button>
                    </div>
                  ))}
                  <div className="w-16 h-10 m-2 rounded-[12px] relative shadow-md hover:scale-110 transform transition-transform">
                    <label
                      htmlFor="customColorPicker"
                      className="w-full h-full block cursor-pointer rounded-[12px] relative"
                      style={{
                        background:
                          customColor ||
                          "linear-gradient(90deg, red, orange, yellow, green, blue, indigo, violet)",
                      }}
                    >
                      <span className="sr-only">
                        Select custom website color
                      </span>
                      <input
                        type="color"
                        id="customColorPicker"
                        value={formValues.profileColor}
                        onChange={handleCustomColorSelection}
                        className="opacity-0 absolute inset-0 w-full h-full cursor-pointer"
                        aria-label="Custom profile color picker"
                      />
                    </label>
                  </div>
                </div>
              </div>
            </div>
            {/* ENDPOINT SLUG */}
            <div className="flex flex-col gap-[10px] w-full">
              <p className="text-[16px] font-bold">Website URL</p>
              <div
                className={`default-input w-full group ${
                  formErrors.endpointSlug && "input-error"
                }`}
              >
                <input
                  type="text"
                  name="endpointSlug"
                  placeholder="Profile URL"
                  className="w-full indent-[182px] sm:indent-[190px] focus:p-0 text-[15px] sm:text-[16px]"
                  onChange={handleChangeSlug}
                  value={tempEndpointSlug}
                  maxLength={20}
                />
                <div className="absolute left-[12px] sm:left-[20px] top-[50%] -translate-y-[50%] font-bold text-base">
                  zoee.com/find-a-coach/
                </div>
                {loadingSlugVerification ? (
                  <div className="absolute right-[20px] top-[50%] -translate-y-[50%]">
                    <Loader />
                  </div>
                ) : (
                  <></>
                )}
              </div>
              {formErrors.endpointSlug && (
                <div className="field-error">
                  <SvgWarning />
                  {formErrors.endpointSlug}
                </div>
              )}
            </div>
            {/* TITLE */}
            <div className="flex flex-col gap-[10px] w-full">
              <div className="flex justify-between items-center">
                <p className="text-[16px] font-bold">
                  Title{" "}
                  <span className="font-normal text-sm text-graySlate">
                    Optional
                  </span>
                </p>
                <CharLimit charNumber={50} inputText={formValues.title} />
              </div>
              <div
                className={`default-input w-full group ${
                  formErrors.title && "input-error"
                }`}
              >
                <input
                  type="text"
                  name="title"
                  placeholder="Title"
                  className="w-full"
                  maxLength={50}
                  onChange={handleChange}
                  value={formValues.title}
                />
                <div className="overlay">Title</div>
              </div>
              {formErrors.title && (
                <div className="field-error">
                  <SvgWarning />
                  {formErrors.title}
                </div>
              )}
            </div>
            {/* DESCRIPTION */}
            <div className="flex flex-col gap-[10px] w-full">
              <div className="flex justify-between items-center">
                <p className="text-[16px] font-bold">
                  Intro{" "}
                  <span className="font-normal text-sm text-graySlate">
                    Optional
                  </span>
                </p>
                <CharLimit
                  charNumber={200}
                  inputText={formValues.description}
                />
              </div>
              <div
                className={`w-full ${formErrors.description && "input-error"}`}
              >
                <textarea
                  name="description"
                  onChange={handleChangeTextArea}
                  cols={30}
                  rows={2}
                  className={`resize-none border-[1px] border-solid text-graySlate text-[16px] bg-white w-full py-[16px] px-[20px] rounded-[10px] focus:outline-0 focus:border focus:border-solid focus:border-royalBlue focus:text-black border-grayCloud`}
                  maxLength={200}
                  placeholder="This is where you’ll put a brief bio. We don’t need to know what hospital you were born at, just what makes you a 100% rockstar coach!"
                  value={formValues.description}
                ></textarea>
              </div>
              {formErrors.description && (
                <div className="field-error">
                  <SvgWarning />
                  {formErrors.description}
                </div>
              )}
            </div>
            {/* ABOUT */}
            <div className="flex flex-col gap-[10px] w-full">
              <div className="flex justify-between items-center">
                <p className="text-[16px] font-bold">
                  About{" "}
                  <span className="font-normal text-sm text-graySlate">
                    Optional
                  </span>
                </p>
                <CharLimit charNumber={2600} inputText={formValues.about} />
              </div>
              <div>
                <textarea
                  name="about"
                  onChange={handleChangeTextArea}
                  maxLength={2600}
                  cols={30}
                  rows={8}
                  className="whitespace-pre-line resize-none border-[1px] border-solid text-graySlate text-[16px] bg-white w-full py-[16px] px-[20px] rounded-[10px] focus:outline-0 focus:border focus:border-solid focus:border-royalBlue focus:text-black border-grayCloud"
                  placeholder=""
                  value={formValues.about}
                ></textarea>
              </div>
              {formErrors.about && (
                <div className="field-error">
                  <SvgWarning />
                  {formErrors.about}
                </div>
              )}
            </div>
            {/* Years of Experience */}
            <div className="flex gap-4 w-full">
              <div className="flex flex-col w-[27%]">
                <p className="text-16 font-bold">Years Experience</p>
                <span className="font-normal text-sm text-graySlate mb-2">
                  Optional
                </span>
                <div className="default-input w-full group">
                  <input
                    type="number"
                    name="yearsExperience"
                    className="w-16"
                    onChange={handleChange}
                    value={
                      coachPublicProfileData?.profile_information
                        ?.yearsExperience === "0"
                        ? null
                        : formValues.yearsExperience
                    }
                    min={0}
                  />
                  <div className="overlay">Years</div>
                </div>
              </div>

              {/* Expertise tags */}
              <div className="w-[73%] flex flex-col items-end">
                <p className="text-16 font-bold mb-2">Areas of Expertise</p>
                <div className="flex flex-wrap h-full justify-end">
                  <Button
                    type="button"
                    onClick={() => {
                      setTagModal(true)
                    }}
                    className="px-2 border hover:border-grayCloud border-2 rounded-lg mr-1 mt-1 text-sm flex items-center font-bold text-[11px] sm:text-[14px] h-[30px] bg-grayFlash hover-bg-white text-black cursor-pointer"
                    size="sm"
                  >
                    <SvgEdit />
                    Edit
                  </Button>
                  {coachPublicProfileData?.tags?.map(
                    (tag: any, index: number) => (
                      <div
                        className="px-2 border border-2 border-grassGreen rounded-lg mr-1 mt-1 text-sm flex items-center font-bold text-[11px] sm:text-[14px] h-[30px] bg-grassGreen line-clamp-1 text-white"
                        key={index}
                      >
                        <span>
                          <SvgCheck />
                        </span>
                        <p className="ml-1">{tag}</p>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>

            {/* LOCATION */}
            <div className="flex flex-col gap-[10px] w-full">
              <p className="text-[16px] font-bold">
                Location{" "}
                <span className="font-normal text-sm text-graySlate">
                  Optional
                </span>
              </p>
              <Select
                className={`w-full basic-single`}
                classNamePrefix="select"
                value={formValues?.region}
                options={regions}
                menuPlacement="bottom"
                name="region"
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
                onChange={(e: any) =>
                  setFormValues({ ...formValues, region: e })
                }
              />
            </div>
          </div>
          <div className="w-full border border-grayCloud mt-6"></div>
          <div className="flex justify-start items-center mt-4">
            <h2 className="font-bold">Credentials + Education</h2>
            <div className="ml-auto">
              <Button
                variant="link"
                type="button"
                className="transform transition-transform text-black hover:scale-110 hover:text-blurple"
                onClick={() => {
                  setEditCredentials(true)
                }}
              >
                <SvgChevronRight />
              </Button>
            </div>
          </div>

          <p className="text-sm">
            This is where you share your education, certifications, and
            credentials
          </p>
          <div className="w-full border border-grayCloud mt-4"></div>
          <div className="flex justify-start items-center mt-4">
            <h2 className="font-bold">External Links</h2>
            <div className="ml-auto">
              <Button
                variant="link"
                type="button"
                className="transform transition-transform text-black hover:scale-110 hover:text-blurple"
                onClick={() => {
                  setEditSocialMedia(true)
                }}
              >
                <SvgChevronRight />
              </Button>
            </div>
          </div>

          <p className="text-sm">
            This is where your social media and website URLs go
          </p>
          <div className="w-full border border-grayCloud mt-4"></div>
        </form>
        <div className="flex flex-col w-full sm:px-12">
          <div className="flex justify-center sm:justify-end items-center mt-8 gap-4">
            <Button
              size="lg"
              variant="utility"
              onClick={() => {
                setEditProfileInformation(false)
              }}
            >
              Cancel
            </Button>
            {loading ? (
              <Loader />
            ) : (
              <Button
                size="lg"
                type="submit"
                value="Save"
                onClick={handleSubmit}
              >
                Save
              </Button>
            )}
          </div>
        </div>
      </div>{" "}
      {editSocialMedia && (
        <EditSocialMedia links={socialLinks} getSocialMedia={getSocialMedia} />
      )}
      {tagModal && (
        <TagsModal
          setTagsUpdated={setTagsUpdated}
          setTagsModalOpen={setTagModal}
        />
      )}
    </div>
  )
}

export default EditProfileInformation
